/**
 * Admin — Field Agent Weekly Rollup (READ-ONLY)
 * /admin/field-agents/:agent_id/weekly-rollup
 * Phase 2C (2026-04-23)
 *
 * Uses the existing /api/field-activities/weekly-rollup endpoint with agent_id.
 * NO write actions. Click-through only to read-only activity detail.
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Loader2, ArrowLeft, Image as ImageIcon, AlertTriangle,
  CalendarCheck2, ChevronRight, MapPin
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-JM", { month: "short", day: "numeric" });
}

export default function AdminFieldAgentWeeklyRollup() {
  const { agent_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rollups, setRollups] = useState([]);
  const [agentName, setAgentName] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [roll, agents] = await Promise.all([
          api.get(`/field-activities/weekly-rollup?agent_id=${agent_id}&weeks=8`),
          api.get("/admin/field-agents"),
        ]);
        setRollups(roll.data.rollups || []);
        const a = (agents.data.agents || []).find((x) => x.id === agent_id);
        if (a) setAgentName(a.name || a.email);
      } catch (e) {
        setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load rollup");
      } finally {
        setLoading(false);
      }
    })();
  }, [agent_id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="admin-weekly-rollup-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-5">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/field-agents")}><ArrowLeft className="w-4 h-4 mr-1" /> All agents</Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{agentName || agent_id}</h1>
            <p className="text-sm text-slate-500">Weekly rollup · 8 weeks · America/Jamaica</p>
          </div>
          <Badge variant="outline" data-testid="admin-rollup-readonly">Read-only</Badge>
        </div>

        {error && <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 mb-3" data-testid="admin-rollup-error">{String(error)}</div>}

        <div className="space-y-3">
          {rollups.map((r, idx) => (
            <section key={r.week_of} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`admin-rollup-week-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold">{formatDate(r.week_of)} – {formatDate(r.week_end)}</h2>
                  <p className="text-xs text-slate-500">{idx === 0 ? "This week" : `${idx} week${idx > 1 ? "s" : ""} ago`}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/field-agents/${agent_id}/activities?week_of=${r.week_of}`)} data-testid={`admin-rollup-activities-${idx}`}>
                  View activities <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>

              <div className="grid grid-cols-4 gap-2 text-sm mb-3">
                <div className="bg-slate-100 rounded-lg p-2 text-center">
                  <div className="font-semibold text-slate-800 text-lg">{r.total_activities}</div>
                  <div className="text-xs text-slate-500">total</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2 text-center">
                  <div className="font-semibold text-emerald-700 text-lg">{r.by_status?.submitted || 0}</div>
                  <div className="text-xs text-emerald-600/70">submitted</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                  <div className="font-semibold text-amber-700 text-lg">{r.by_status?.draft || 0}</div>
                  <div className="text-xs text-amber-600/70">drafts</div>
                </div>
                <div className="bg-slate-100 rounded-lg p-2 text-center">
                  <div className="font-semibold text-slate-800 text-lg">{r.days_active}</div>
                  <div className="text-xs text-slate-500">days active</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 text-xs mb-2">
                <span className="inline-flex items-center gap-1 text-slate-600"><ImageIcon className="w-3 h-3" /> {r.evidence_total} photos · {r.activities_with_evidence} w/ · {r.activities_missing_evidence} missing</span>
                {r.activities_with_duplicate_flag > 0 && <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" /> {r.activities_with_duplicate_flag} duplicate-flagged</span>}
                {r.follow_ups_due_this_week > 0 && <span className="inline-flex items-center gap-1 text-indigo-700"><CalendarCheck2 className="w-3 h-3" /> {r.follow_ups_due_this_week} follow-ups due</span>}
              </div>

              {r.total_activities > 0 && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-[11px] font-medium text-slate-500 mb-1">Prospect type</div>
                    <div className="flex flex-wrap gap-1">{Object.entries(r.by_prospect_type || {}).map(([k, v]) => <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-slate-500 mb-1">Outcome</div>
                    <div className="flex flex-wrap gap-1">{Object.entries(r.by_visit_outcome || {}).map(([k, v]) => <Badge key={k} variant="outline" className="text-[10px]">{k.replace(/_/g, " ")}: {v}</Badge>)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-slate-500 mb-1">Parish</div>
                    <div className="flex flex-wrap gap-1">{Object.entries(r.by_parish || {}).map(([k, v]) => <Badge key={k} variant="outline" className="text-[10px]"><MapPin className="w-2 h-2 mr-0.5 inline" />{k}: {v}</Badge>)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-slate-500 mb-1">Conversion intent</div>
                    <div className="flex flex-wrap gap-1">{Object.entries(r.by_conversion_intent || {}).map(([k, v]) => <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>)}</div>
                  </div>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
